import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const Chip = ({ children, label }) => (
  <span className="h-10 w-14 grid place-items-center rounded-xl bg-white border border-stone-200 shadow-sm" aria-label={label} title={label}>{children}</span>
);

const CardLogos = () => (
  <div className="flex items-center justify-center gap-3" dir="ltr" data-testid="subscribe-card-logos">
    <Chip label="Mastercard">
      <svg viewBox="0 0 32 20" className="w-8 h-5"><circle cx="12" cy="10" r="8" fill="#EB001B" /><circle cx="20" cy="10" r="8" fill="#F79E1B" /><path d="M16 3.8a8 8 0 0 1 0 12.4 8 8 0 0 1 0-12.4z" fill="#FF5F00" /></svg>
    </Chip>
    <Chip label="Visa"><span className="font-black italic text-[15px] tracking-tight text-[#1A1F71]">VISA</span></Chip>
    <Chip label="American Express"><span className="bg-[#2E77BC] text-white text-[9px] font-black px-1.5 py-1 rounded-sm leading-none">AMEX</span></Chip>
  </div>
);

const PAYPAL_LOCALE = { ar: "ar_EG", en: "en_US", fr: "fr_FR", es: "es_ES" };

export const SubscribePanel = ({ renewing = false }) => {
  const { user, setUser } = useAuth();
  const { t, lang } = useLang();
  const s = t.sub;
  const [conf, setConf] = useState(null);
  const expired = !renewing && Boolean(user?.subscription_until);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setConf(r.data)).catch(() => setConf({ enabled: false }));
  }, []);

  const createOrder = async () => (await api.post("/paypal/orders")).data.id;
  const onApprove = async ({ orderID }) => {
    try {
      const { data } = await api.post(`/paypal/orders/${orderID}/capture`);
      setUser(data);
      toast.success(s.success);
    } catch {
      toast.error(s.error);
    }
  };
  const btn = { createOrder, onApprove, onError: () => toast.error(s.error) };

  return (
    <section className="rounded-2xl border-2 border-[#D9A3A0] bg-[#FBF3F0] p-4 sm:p-5 max-w-3xl" data-testid="subscribe-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-bold text-lg text-[#B91C1C]" data-testid="subscribe-title">
            <AlertTriangle className="w-5 h-5 shrink-0" /> {renewing ? s.renewTitle : expired ? s.expired : s.title}
          </h2>
          <p className="text-sm font-semibold text-[#1C1917] mt-1">{renewing ? s.soonBody : expired ? s.renew : s.body()}</p>
        </div>
        <div className="shrink-0 text-center" data-testid="subscribe-price">
          <p className="text-xl sm:text-2xl font-black text-[#1C1917] whitespace-nowrap" data-testid="subscribe-price-sar">{s.price}</p>
          <p className="text-xs text-stone-600">{s.per}</p>
          <p className="text-xs text-stone-600 mt-1 max-w-[8rem]" data-testid="subscribe-price-usd">{s.usd}</p>
        </div>
      </div>

      <p className="text-center text-sm font-semibold text-stone-700 mt-5 mb-3">{s.payNote}</p>
      <CardLogos />

      <div className="mt-4" data-testid="subscribe-paypal">
        {conf?.enabled && conf.client_id ? (
          <PayPalScriptProvider key={lang} options={{ clientId: conf.client_id, currency: conf.currency, intent: "capture", components: "buttons", enableFunding: "card", locale: PAYPAL_LOCALE[lang] || "en_US" }}>
            <div className="space-y-2">
              <PayPalButtons fundingSource={FUNDING.CARD} style={{ shape: "pill", color: "black", height: 48 }} {...btn} />
              <p className="text-center text-xs text-stone-600" dir="ltr">Powered by <b className="text-[#003087] italic">Pay</b><b className="text-[#009CDE] italic">Pal</b></p>
              <PayPalButtons fundingSource={FUNDING.PAYPAL} style={{ shape: "pill", color: "gold", height: 48 }} {...btn} />
            </div>
          </PayPalScriptProvider>
        ) : conf && (
          <p className="text-sm bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-xl" data-testid="subscribe-unavailable">{s.unavailable}</p>
        )}
      </div>
    </section>
  );
};
